import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { AlertTriangle, Banknote, Bike, CreditCard, Loader2, Smartphone } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ChoiceCard } from '@/components/ui/choice-card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  answersCoverageOnly,
  DELIVERY_CUSTOMER_PHONE_REQUIRED,
  DELIVERY_PAYMENT_METHOD_UNSUPPORTED,
  DELIVERY_PROVIDER_NOT_BOUND,
  deliveryErrorMessage,
  deliveryProviderDefinition,
  isFailedQuote,
  isDeliveryErrorCode,
  localHoldLeadOf,
  quoteBlocksRequest,
  quoteHasPrice,
  supportsOf,
  type DeliveryPaymentMode,
  type DeliveryProviderSupports,
} from '@/lib/delivery-providers-client';
import {
  amountOf,
  deliveriesClient,
  isCustomerPhoneRejection,
  orderDeliveryClient,
  type DeliveryDispatchDto,
} from '@/lib/deliveries-client';
import { formatMoney } from '@/lib/money';
import type { Order } from '@/lib/orders-client';
import { cn } from '@/lib/utils';
import {
  availablePaymentModes,
  clampPrepMinutes,
  isHeldForLater,
  isLookupablePhone,
  isUsableCustomerPhone,
  MAX_PREP_TIME_MINUTES,
  normalizePhone,
  orderPaymentBlocked,
  orderPaymentMode,
} from './delivery-form';
import { DeliveryCoverageLine } from './delivery-coverage';

const PAYMENT_ICONS: Record<DeliveryPaymentMode, typeof Banknote> = {
  cash: Banknote,
  online: Smartphone,
  terminal: CreditCard,
};

/** Bring the phone field to the operator: the dialog body scrolls, and the field may be below the fold. */
function focusPhoneInput(input: HTMLInputElement | null) {
  if (!input) return;
  input.focus({ preventScroll: true });
  input.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/** The order's pin, when its address was placed on the map. */
export function orderCoordinates(order: Pick<Order, 'deliveryAddress'>): { lat: number; lng: number } | null {
  const coordinates = order.deliveryAddress?.coordinates as { lat?: unknown; lng?: unknown } | undefined;
  if (typeof coordinates?.lat === 'number' && typeof coordinates?.lng === 'number') {
    return { lat: coordinates.lat, lng: coordinates.lng };
  }
  return null;
}

interface RequestRiderDialogProps {
  order: Order | null;
  /** The branch's provider `type` — its registry entry names it. */
  providerType: string | null | undefined;
  /** The brand has its own fleet on the provider: `terminal` can travel. */
  myFleet: boolean;
  /** The branch's effective prep time (capabilities) — the field starts on it; 0 = look now. */
  defaultPrepTimeMinutes?: number | null;
  /** What the branch's provider supports (`supportsOf` its capabilities entry); the registry's when unset. */
  supports?: DeliveryProviderSupports;
  /** Minutes before the order is ready Menteo asks for the courier; the registry's when unset. */
  courierLeadMinutes?: number;
  onOpenChange: (open: boolean) => void;
  onRequested: (dispatch: DeliveryDispatchDto) => void;
}

/**
 * "Pedir repartidor" for an order (spec §4.2 manual dispatch): the live quote, when to look for the
 * rider (0 = now), how the rider is paid — defaulted from how the customer pays — and instructions.
 * The booking itself is created in the background; the section follows it from there.
 */
export function RequestRiderDialog({
  order,
  providerType,
  myFleet,
  defaultPrepTimeMinutes,
  supports: supportsProp,
  courierLeadMinutes,
  onOpenChange,
  onRequested,
}: RequestRiderDialogProps) {
  const { t, i18n } = useTranslation('orders');
  const provider = deliveryProviderDefinition(providerType);
  const supports = supportsProp ?? supportsOf(providerType);
  // A provider without `QUOTE` that checks coverage: the cost block becomes a coverage line.
  const coverageOnly = answersCoverageOnly(supports);
  // Menteo holds the booking for a provider without `HOLD`: the courier is asked for this long before ready.
  const courierLead = courierLeadMinutes ?? localHoldLeadOf(providerType);
  const defaultMode = orderPaymentMode(order?.paymentPreference);
  const [paymentMode, setPaymentMode] = useState<DeliveryPaymentMode>(defaultMode);
  const [prepTime, setPrepTime] = useState('0');
  const [instructions, setInstructions] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  /** The API answered `DELIVERY_CUSTOMER_PHONE_REQUIRED` for a phone this build took as usable. */
  const [phoneDemanded, setPhoneDemanded] = useState(false);
  /** The field was left once: from then on an invalid phone is said, not only while submit is off. */
  const [phoneTouched, setPhoneTouched] = useState(false);
  /** The API refused the typed phone (400): its sentence, until the phone is edited. */
  const [phoneServerError, setPhoneServerError] = useState<string | null>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset when the dialog opens for an order — keyed on its id: the board feed hands the panel a
  // fresh copy of the same order on every change, which must not wipe what the operator typed.
  const orderId = order?.id ?? null;
  const orderPreference = order?.paymentPreference;
  useEffect(() => {
    if (!orderId) return;
    setPaymentMode(orderPaymentMode(orderPreference));
    setPrepTime(String(defaultPrepTimeMinutes ?? 0));
    setInstructions('');
    setCustomerPhone('');
    setPhoneDemanded(false);
    setPhoneTouched(false);
    setPhoneServerError(null);
    setSubmitting(false);
    // Only the open transition (a new order id) resets; a refreshed copy keeps the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const coordinates = order ? orderCoordinates(order) : null;
  // No phone the rider can call (none, or an Instagram id): the operator types one for this delivery.
  const asksPhone = !!order && (phoneDemanded || !isUsableCustomerPhone(order.customer?.phone));
  const phoneValid = !asksPhone || isLookupablePhone(customerPhone);
  const phoneError =
    phoneServerError ?? (phoneTouched && !phoneValid ? t('delivery.newDelivery.validation.phone') : undefined);

  // The field just appeared because the API asked for it (409): take the operator straight to it.
  useEffect(() => {
    if (phoneDemanded) focusPhoneInput(phoneInputRef.current);
  }, [phoneDemanded]);

  const blocked = orderPaymentBlocked(order?.paymentPreference, myFleet);
  // D7: a `terminal` run without own fleet is refused by the server (409) — and so it is here.
  const terminalRefused = blocked && paymentMode === 'terminal';
  // `terminal` is offered with own fleet — and shown, flagged, when it is what the order asks for.
  const modes = availablePaymentModes(myFleet);
  const offeredModes: DeliveryPaymentMode[] = modes.includes(defaultMode) ? modes : [...modes, defaultMode];

  const quote = useQuery({
    queryKey: ['delivery-quote', order?.locationId, coordinates?.lat, coordinates?.lng, paymentMode],
    queryFn: () =>
      deliveriesClient.quote({
        locationId: order!.locationId!,
        lat: coordinates!.lat,
        lng: coordinates!.lng,
        paymentMode,
      }),
    enabled: !!order?.locationId && !!coordinates,
    staleTime: 60_000,
    retry: false,
  });

  const handleSubmit = async () => {
    if (!order) return;
    setSubmitting(true);
    try {
      const dispatch = await orderDeliveryClient.request(order.id, {
        prepTimeMinutes: clampPrepMinutes(prepTime),
        paymentMode,
        ...(instructions.trim() ? { riderInstructions: instructions.trim() } : {}),
        ...(asksPhone ? { customerPhone: normalizePhone(customerPhone) } : {}),
      });
      // Held by Menteo until the courier lead before ready: say when the rider is asked for.
      toast.success(
        dispatch.bookAfter
          ? t('delivery.section.requestScheduled', {
              provider: provider.name,
              time: format(new Date(dispatch.bookAfter), 'HH:mm'),
            })
          : t('delivery.section.requestDone', { provider: provider.name }),
      );
      onRequested(dispatch);
    } catch (err) {
      if (isDeliveryErrorCode(err, DELIVERY_PAYMENT_METHOD_UNSUPPORTED)) {
        toast.error(deliveryErrorMessage(err) ?? t('delivery.errors.DELIVERY_PAYMENT_METHOD_UNSUPPORTED'));
      } else if (isDeliveryErrorCode(err, DELIVERY_PROVIDER_NOT_BOUND)) {
        toast.error(t('delivery.errors.DELIVERY_PROVIDER_NOT_BOUND'));
      } else if (isDeliveryErrorCode(err, DELIVERY_CUSTOMER_PHONE_REQUIRED)) {
        // The server found no usable phone where this build saw one: ask for it here.
        setPhoneDemanded(true);
        toast.error(deliveryErrorMessage(err) ?? t('delivery.errors.DELIVERY_CUSTOMER_PHONE_REQUIRED'));
      } else if (asksPhone && isCustomerPhoneRejection(err)) {
        // The typed phone was refused: said on the field itself, which is where the fix goes. The
        // route's own sentence when it sent one — a validation list is developer English, so ours.
        const message = err.data.message;
        setPhoneServerError(
          typeof message === 'string' && message.trim() ? message : t('delivery.newDelivery.validation.phone'),
        );
        focusPhoneInput(phoneInputRef.current);
      } else {
        toast.error(deliveryErrorMessage(err) ?? t('delivery.section.actionFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const address = (order?.deliveryAddress?.raw as string | undefined) ?? '';
  // Menteo will hold this request for later: an out-of-hours answer then warns instead of blocking.
  const heldForLater = isHeldForLater(clampPrepMinutes(prepTime), courierLead, supports);

  return (
    <Dialog open={!!order} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="border-b border-border px-5 py-4 text-left">
          <DialogTitle className="flex items-center gap-2">
            <Bike className="h-4 w-4 text-primary" />
            {t('delivery.requestDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('delivery.requestDialog.description', { id: order?.id.slice(0, 8) ?? '', provider: provider.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {address && <p className="text-sm text-foreground">{address}</p>}

          {!coordinates && (
            <Alert variant="warning">
              <AlertTriangle />
              <AlertDescription>
                <p>{t('delivery.requestDialog.noCoordinates', { provider: provider.name })}</p>
              </AlertDescription>
            </Alert>
          )}

          {asksPhone && (
            <Field
              htmlFor="request-rider-phone"
              label={t('delivery.requestDialog.customerPhone')}
              required
              hint={t('delivery.requestDialog.customerPhoneHint')}
              error={phoneError}
            >
              <Input
                ref={phoneInputRef}
                id="request-rider-phone"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                required
                aria-required="true"
                aria-invalid={phoneError ? true : undefined}
                placeholder={t('delivery.newDelivery.phonePlaceholder')}
                value={customerPhone}
                onChange={(event) => {
                  setCustomerPhone(event.target.value);
                  setPhoneServerError(null);
                }}
                onBlur={() => setPhoneTouched(true)}
              />
            </Field>
          )}

          {coverageOnly ? (
            <DeliveryCoverageLine
              providerName={provider.name}
              quote={quote.data}
              idle={coordinates ? null : '—'}
              heldForLater={heldForLater}
              pending={quote.isFetching && !quote.data}
              errorText={
                quote.isError
                  ? (deliveryErrorMessage(quote.error) ??
                    t('delivery.newDelivery.coverageUnavailable', { provider: provider.name }))
                  : null
              }
            />
          ) : (
            <div
              className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2.5"
              aria-live="polite"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t('delivery.newDelivery.providerCost', { provider: provider.name })}
                </p>
                {quote.data?.covered && quote.data.etaMinutes != null && (
                  <p className="text-xs text-muted-foreground">
                    {t('delivery.newDelivery.eta', { minutes: quote.data.etaMinutes })}
                  </p>
                )}
              </div>
              <div className="text-right text-sm">
                {!coordinates ? (
                  <span className="text-xs text-muted-foreground">—</span>
                ) : quote.isFetching && !quote.data ? (
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t('delivery.newDelivery.quoting')}
                  </span>
                ) : quote.isError ? (
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    {deliveryErrorMessage(quote.error) ?? t('delivery.newDelivery.quoteUnavailable')}
                  </span>
                ) : isFailedQuote(quote.data) ? (
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    {t('delivery.newDelivery.quoteUnavailable')}
                  </span>
                ) : quote.data && !quote.data.covered ? (
                  <span className="block text-xs font-medium text-red-700 dark:text-red-300">
                    {t('delivery.newDelivery.notCovered', { provider: provider.name })}
                    {quote.data.detail && (
                      <span className="mt-0.5 block text-[11px] font-normal break-words text-muted-foreground">
                        {quote.data.detail}
                      </span>
                    )}
                  </span>
                ) : quoteHasPrice(quote.data) ? (
                  <span className="font-semibold text-foreground tabular-nums">
                    {formatMoney(
                      amountOf(quote.data.providerFee ?? quote.data.fee),
                      quote.data.currency ?? order?.currency,
                      i18n.language,
                    )}
                  </span>
                ) : quote.data ? (
                  // Covered without a fee: never a money figure for `fee: null`.
                  <span className="text-xs text-muted-foreground">—</span>
                ) : null}
              </div>
            </div>
          )}

          {isFailedQuote(quote.data) && (
            <Alert variant="warning">
              <AlertTriangle />
              <AlertDescription>
                <p>
                  {coverageOnly
                    ? quote.data?.detail
                      ? t('delivery.newDelivery.coverageFailedWarning', {
                          provider: provider.name,
                          detail: quote.data.detail,
                        })
                      : t('delivery.newDelivery.coverageFailedWarningNoDetail', { provider: provider.name })
                    : quote.data?.detail
                      ? t('delivery.newDelivery.quoteFailedWarning', {
                          provider: provider.name,
                          detail: quote.data.detail,
                        })
                      : t('delivery.newDelivery.quoteFailedWarningNoDetail', { provider: provider.name })}
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* With a courier lead the field is when the order is READY: the courier is asked for earlier. */}
          <Field
            htmlFor="request-rider-prep"
            label={courierLead > 0 ? t('delivery.newDelivery.prepTimeReady') : t('delivery.newDelivery.prepTime')}
            hint={
              courierLead > 0
                ? t('delivery.newDelivery.prepTimeLeadHint', { lead: courierLead })
                : t('delivery.newDelivery.prepTimeHint')
            }
          >
            <Input
              id="request-rider-prep"
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_PREP_TIME_MINUTES}
              className="w-28"
              value={prepTime}
              onChange={(event) => setPrepTime(event.target.value)}
            />
          </Field>

          <div className="space-y-2">
            <p id="request-rider-payment-label" className="text-sm font-medium text-foreground">
              {t('delivery.newDelivery.paymentMode')}
            </p>
            <p className="text-xs text-muted-foreground">{t('delivery.requestDialog.paymentFromOrder')}</p>
            <div
              role="radiogroup"
              aria-labelledby="request-rider-payment-label"
              className={cn('grid gap-2', offeredModes.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}
            >
              {offeredModes.map((mode) => (
                <ChoiceCard
                  key={mode}
                  className="p-3"
                  icon={PAYMENT_ICONS[mode]}
                  selected={paymentMode === mode}
                  onSelect={() => setPaymentMode(mode)}
                  title={t(`delivery.paymentModeShort.${mode}`)}
                  description={t(`delivery.paymentModeHint.${mode}`)}
                />
              ))}
            </div>
            {terminalRefused && (
              <Alert variant="warning">
                <AlertTriangle />
                <AlertDescription>
                  <p>{t('delivery.errors.DELIVERY_PAYMENT_METHOD_UNSUPPORTED')}</p>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <Field htmlFor="request-rider-instructions" label={t('delivery.newDelivery.instructions')}>
            <Textarea
              id="request-rider-instructions"
              rows={2}
              maxLength={250}
              placeholder={t('delivery.newDelivery.instructionsPlaceholder')}
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
            />
          </Field>
        </div>

        <DialogFooter className="border-t border-border px-5 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('delivery.newDelivery.cancel')}
          </Button>
          {/* Refusals the server would answer are refused here: no pin, not covered (known only once
              the quote answered; out of hours only when the request goes out now), no phone to call,
              a terminal without own fleet. */}
          <Button
            onClick={() => void handleSubmit()}
            disabled={
              submitting ||
              !coordinates ||
              quote.isFetching ||
              quoteBlocksRequest(quote.data, heldForLater) ||
              !phoneValid ||
              !!phoneServerError ||
              terminalRefused
            }
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bike className="h-4 w-4" />}
            {t('delivery.requestDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
